import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search,
  X,
  ChevronDown,
  Calendar,
  Download,
  Filter,
  FileText,
  FileSpreadsheet,
  BarChart3,
  Package,
  RefreshCw,
  SortAsc,
  Grid,
  Hash,
  Database,
  CheckCircle,
  ChevronUp,
  ChevronRight,
  ChefHat,
  Users,
  Building2,
  Eye,
  EyeOff,
  ChevronLeft,
  Printer,
  Layers,
  BarChart,
  Home,
  Store,
  TrendingUp
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { reportsAPI, chefsAPI, branchesAPI } from '../services/api';
import { toast } from 'react-toastify';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import {
  startOfDay,
  endOfDay,
  subDays,
  subMonths,
  format,
  parseISO,
  isSameDay,
  addHours,
} from 'date-fns';
import { arSA, enUS } from 'date-fns/locale';

// ==============================
// === INTERFACES ===
// ==============================
interface Chef {
  _id: string;
  user: {
    _id: string;
    name: string;
    nameEn: string;
    username: string;
    email: string;
  };
  department: Array<{
    _id: string;
    name: string;
    nameEn: string;
    code: string;
  }>;
  createdAt: string;
  updatedAt: string;
}

interface BranchProductData {
  id: string;
  name: string;
  assigned: number;
  completed: number;
  percentage?: number;
}

interface ProductData {
  id: string;
  name: string;
  code: string;
  totalAssigned: number;
  totalCompleted: number;
  unit: string;
  department?: string;
  departmentId?: string;
  branches?: BranchProductData[];
  branchDistribution?: { [branchId: string]: number };
}

interface ChefProductionData {
  id: string;
  name: string;
  email: string;
  isActive: boolean;
  departments: Array<{
    id: string;
    name: string;
    nameEn: string;
    code: string;
  }>;
  status: string;
  
  totalAssignedQuantity: number;
  totalCompletedQuantity: number;
  completionRate: number;
  
  products: {
    [productId: string]: ProductData;
  };
  
  branchSummary?: {
    [branchId: string]: {
      assigned: number;
      completed: number;
      products: number;
    };
  };
}

interface Branch {
  id: string;
  name: string;
  nameEn: string;
  code: string;
  color?: string;
}

interface CacheEntry {
  data: any;
  timestamp: number;
  ttl: number;
}

// ==============================
// === CACHE MANAGEMENT ===
// ==============================
class DataCache {
  private cache: Map<string, CacheEntry>;
  private maxSize: number;
  private cleanupInterval: NodeJS.Timeout | null;

  constructor(maxSize: number = 50) {
    this.cache = new Map();
    this.maxSize = maxSize;
    this.cleanupInterval = setInterval(() => this.cleanup(), 60000);
  }

  get(key: string): any | null {
    const entry = this.cache.get(key);
    if (!entry) return null;

    if (Date.now() - entry.timestamp > entry.ttl) {
      this.cache.delete(key);
      return null;
    }

    return entry.data;
  }

  set(key: string, data: any, ttl: number = 5 * 60 * 1000): void {
    if (this.cache.size >= this.maxSize) {
      const firstKey = this.cache.keys().next().value;
      if (firstKey) this.cache.delete(firstKey);
    }

    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl,
    });
  }

  delete(key: string): void {
    this.cache.delete(key);
  }

  clear(): void {
    this.cache.clear();
  }

  private cleanup(): void {
    const now = Date.now();
    for (const [key, entry] of this.cache.entries()) {
      if (now - entry.timestamp > entry.ttl) {
        this.cache.delete(key);
      }
    }
  }

  destroy(): void {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
    }
  }
}

const globalCache = new DataCache(50);

// ==============================
// === UTILITY FUNCTIONS ===
// ==============================
const formatNumberWithComma = (num: number): string => {
  if (isNaN(num) || num === null || num === undefined) return '0';
  
  if (num % 1 !== 0) {
    const rounded = Math.round(num * 100) / 100;
    const parts = rounded.toString().split('.');
    const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const decimalPart = parts[1] ? parts[1].padEnd(2, '0').substring(0, 2) : '00';
    return `${integerPart}.${decimalPart}`;
  }
  
  return Math.round(num).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

const toArabicNumerals = (number: string | number): string => {
  if (number === null || number === undefined) return '0';
  const arabicNumerals = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];
  
  const numberStr = String(number);
  const parts = numberStr.split('.');
  
  const integerPart = parts[0].replace(/[0-9]/g, (digit) => arabicNumerals[parseInt(digit)]);
  
  if (parts[1]) {
    const decimalPart = parts[1].replace(/[0-9]/g, (digit) => arabicNumerals[parseInt(digit)]);
    return `${integerPart}.${decimalPart}`;
  }
  
  return integerPart;
};

const formatNumber = (num: number, isRtl: boolean): string => {
  const formatted = formatNumberWithComma(num);
  return isRtl ? toArabicNumerals(formatted) : formatted;
};

const formatPercentage = (num: number, isRtl: boolean): string => {
  const formatted = Math.round(num * 100) / 100;
  return `${formatNumber(formatted, isRtl)}%`;
};

const fixArabicText = (text: string): string => {
  if (!text || typeof text !== 'string') return text || '';
  
  let fixedText = text.trim();
  
  fixedText = fixedText
    .replace(/[\u200B-\u200D\uFEFF]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  
  return fixedText;
};

const calculatePeriodDates = (period: string) => {
  const now = new Date();
  let start: Date, end: Date;

  switch (period) {
    case 'today':
      start = addHours(startOfDay(now), 12);
      end = endOfDay(now);
      break;
    case 'yesterday':
      const yesterday = subDays(now, 1);
      start = addHours(startOfDay(yesterday), 12);
      end = endOfDay(yesterday);
      break;
    case 'week':
      start = startOfDay(subDays(now, 6));
      end = endOfDay(now);
      break;
    case 'month':
      start = startOfDay(subMonths(now, 1));
      end = endOfDay(now);
      break;
    default:
      start = addHours(startOfDay(now), 12);
      end = endOfDay(now);
  }

  return { start, end };
};

const getQuantityColor = (quantity: number, isCompleted: boolean = false): string => {
  if (quantity === 0) return 'text-gray-400';
  
  if (isCompleted) {
    if (quantity <= 10) return 'text-emerald-600';
    if (quantity <= 50) return 'text-green-600';
    if (quantity <= 100) return 'text-green-700';
    return 'text-emerald-800';
  } else {
    if (quantity <= 10) return 'text-amber-600';
    if (quantity <= 50) return 'text-orange-600';
    if (quantity <= 100) return 'text-orange-700';
    return 'text-amber-800';
  }
};

const getQuantityBgColor = (quantity: number, isCompleted: boolean = false): string => {
  if (quantity === 0) return 'bg-gray-50';
  
  if (isCompleted) {
    if (quantity <= 10) return 'bg-emerald-50';
    if (quantity <= 50) return 'bg-green-50';
    if (quantity <= 100) return 'bg-green-100';
    return 'bg-emerald-100';
  } else {
    if (quantity <= 10) return 'bg-amber-50';
    if (quantity <= 50) return 'bg-orange-50';
    if (quantity <= 100) return 'bg-orange-100';
    return 'bg-amber-100';
  }
};

const getRandomColor = (index: number): string => {
  const colors = [
    'bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-pink-500', 
    'bg-indigo-500', 'bg-teal-500', 'bg-orange-500', 'bg-cyan-500',
    'bg-red-500', 'bg-yellow-500', 'bg-lime-500', 'bg-fuchsia-500'
  ];
  return colors[index % colors.length];
};

// ==============================
// === PDF FONT LOADER ===
// ==============================
const loadAmiriFont = async (doc: jsPDF): Promise<boolean> => {
  const fontName = 'Amiri';
  try {
    const [regularRes, boldRes] = await Promise.all([
      fetch('https://cdn.jsdelivr.net/gh/aliftype/amiri@main/fonts/Amiri-Regular.ttf'),
      fetch('https://cdn.jsdelivr.net/gh/aliftype/amiri@main/fonts/Amiri-Bold.ttf'),
    ]);
    const [regularFontBytes, boldFontBytes] = await Promise.all([
      regularRes.arrayBuffer(),
      boldRes.arrayBuffer(),
    ]);
    const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
      let binary = '';
      const bytes = new Uint8Array(buffer);
      for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
      return window.btoa(binary);
    };
    doc.addFileToVFS(`${fontName}-normal.ttf`, arrayBufferToBase64(regularFontBytes));
    doc.addFont(`${fontName}-normal.ttf`, fontName, 'normal');
    doc.addFileToVFS(`${fontName}-bold.ttf`, arrayBufferToBase64(boldFontBytes));
    doc.addFont(`${fontName}-bold.ttf`, fontName, 'bold');
    return true;
  } catch (error) {
    console.warn('Failed to load Amiri font. Using fallback.');
    return false;
  }
};

// ==============================
// === تحسين بيانات الفروع للمنتجات ===
// ==============================
const enhanceProductWithBranchData = (
  product: ProductData, 
  branches: Branch[]
): ProductData => {
  if (!product.branches || !Array.isArray(product.branches)) {
    const enhancedBranches = branches.map(branch => ({
      id: branch.id,
      name: branch.name,
      assigned: 0,
      completed: 0,
      percentage: 0
    }));
    
    return {
      ...product,
      branches: enhancedBranches,
      branchDistribution: branches.reduce((acc, branch) => {
        acc[branch.id] = 0;
        return acc;
      }, {} as { [key: string]: number })
    };
  }
  
  // تأكد من أن جميع الفروع موجودة
  const existingBranchIds = new Set(product.branches.map(b => b.id));
  const missingBranches = branches.filter(b => !existingBranchIds.has(b.id));
  
  const enhancedBranches = [
    ...product.branches,
    ...missingBranches.map(branch => ({
      id: branch.id,
      name: branch.name,
      assigned: 0,
      completed: 0,
      percentage: 0
    }))
  ];
  
  // حساب التوزيع النسبي
  const totalAssigned = enhancedBranches.reduce((sum, b) => sum + b.assigned, 0);
  const enhancedBranchesWithPercentage = enhancedBranches.map(branch => ({
    ...branch,
    percentage: totalAssigned > 0 ? (branch.assigned / totalAssigned) * 100 : 0
  }));
  
  const branchDistribution = enhancedBranchesWithPercentage.reduce((acc, branch) => {
    acc[branch.id] = branch.percentage;
    return acc;
  }, {} as { [key: string]: number });
  
  return {
    ...product,
    branches: enhancedBranchesWithPercentage,
    branchDistribution
  };
};

// ==============================
// === تصدير PDF متوافق مع العربية ===
// ==============================
const exportToPDF = async (
  title: string,
  headers: string[],
  body: any[][],
  isRtl: boolean,
  includeFooter: boolean = true,
  fileName: string
): Promise<void> => {
  const toastId = 'pdf-export';
  try {
    toast.info(isRtl ? 'جارٍ إنشاء ملف PDF...' : 'Generating PDF...', {
      position: isRtl ? 'top-left' : 'top-right',
      autoClose: false,
      toastId,
    });

    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4',
    });

    const fontLoaded = await loadAmiriFont(doc);
    const fontName = fontLoaded ? 'Amiri' : 'helvetica';
    const pageWidth = doc.internal.pageSize.width;
    const margin = isRtl ? 15 : 15;

    doc.setFont(fontName, 'bold');
    doc.setFontSize(16);
    doc.setTextColor(31, 41, 55);
    
    doc.text(title, isRtl ? pageWidth - margin : margin, 20, { 
      align: isRtl ? 'right' : 'left' 
    });

    doc.setFont(fontName, 'normal');
    doc.setFontSize(10);
    doc.setTextColor(107, 114, 128);
    
    const date = new Date().toLocaleDateString(isRtl ? 'ar-SA' : 'en-US');
    const infoText = isRtl ? `التاريخ: ${date}` : `Date: ${date}`;
    doc.text(infoText, isRtl ? pageWidth - margin : margin, 30, { 
      align: isRtl ? 'right' : 'left' 
    });

    let yPos = 40;

    const columnStyles: any = {};
    const columnCount = headers.length;
    const columnWidth = (pageWidth - (margin * 2)) / columnCount;
    
    for (let i = 0; i < columnCount; i++) {
      columnStyles[i] = { 
        cellWidth: columnWidth,
        halign: 'center',
        valign: 'middle'
      };
    }

    autoTable(doc, {
      head: [headers],
      body: body,
      startY: yPos,
      margin: { 
        left: isRtl ? margin : margin, 
        right: isRtl ? margin : margin 
      },
      theme: 'grid',
      styles: {
        font: fontName,
        fontSize: 9,
        textColor: [31, 41, 55],
        lineColor: [209, 213, 219],
        lineWidth: 0.5,
        cellPadding: 5,
        overflow: 'linebreak',
        halign: 'center',
        valign: 'middle',
        lineHeight: 1.5
      },
      headStyles: {
        fillColor: [59, 130, 246],
        textColor: 255,
        fontSize: 10,
        fontStyle: 'bold',
        halign: 'center',
        valign: 'middle',
        cellPadding: 6,
        lineWidth: 0.5,
        lineColor: [37, 99, 235],
        font: fontName
      },
      bodyStyles: {
        fontSize: 9,
        halign: 'center',
        valign: 'middle',
        cellPadding: 4,
        lineWidth: 0.5,
        overflow: 'linebreak',
        font: fontName,
        lineHeight: 1.5
      },
      columnStyles: columnStyles,
      didParseCell: (data: any) => {
        if (isRtl && typeof data.cell.text === 'string') {
          data.cell.text = data.cell.text.replace(/\d+/g, (match: string) => 
            toArabicNumerals(match)
          );
          
          if (data.cell.text.includes('.')) {
            const parts = data.cell.text.split('.');
            if (parts.length === 2) {
              data.cell.text = `${parts[0]},${parts[1]}`;
            }
          }
        }
        
        if (data.row.index === body.length - 1 && data.section === 'body') {
          data.cell.styles.fillColor = [245, 158, 11];
          data.cell.styles.textColor = [255, 255, 255];
          data.cell.styles.fontStyle = 'bold';
        }
      }
    });

    if (includeFooter) {
      const finalY = (doc as any).lastAutoTable.finalY || yPos + 100;
      doc.setFontSize(8);
      doc.setTextColor(107, 114, 128);
      const footer = isRtl 
        ? '* تم إنشاء هذا التقرير بواسطة نظام إدارة الجوديا'
        : '* This report was generated by aljodia Management System';
      doc.text(footer, pageWidth / 2, finalY + 10, { align: 'center' });
    }

    doc.save(fileName);

    toast.update(toastId, {
      render: isRtl ? 'تم التصدير إلى PDF بنجاح!' : 'Exported to PDF successfully!',
      type: 'success',
      autoClose: 3000,
    });
  } catch (error) {
    console.error('PDF Export Error:', error);
    toast.update(toastId, {
      render: isRtl ? 'فشل تصدير PDF. حاول مرة أخرى.' : 'PDF export failed. Please try again.',
      type: 'error',
      autoClose: 4000,
    });
  }
};

// ==============================
// === تصدير Excel ===
// ==============================
const exportToExcel = async (
  title: string,
  headers: string[],
  body: any[][],
  isRtl: boolean,
  fileName: string
): Promise<void> => {
  const toastId = 'excel-export';
  try {
    toast.info(isRtl ? 'جارٍ إنشاء ملف Excel...' : 'Generating Excel...', {
      position: isRtl ? 'top-left' : 'top-right',
      autoClose: false,
      toastId,
    });

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(isRtl ? 'التقرير' : 'Report', {
      views: [{ rightToLeft: isRtl }]
    });

    // العنوان
    const titleRow = worksheet.addRow([title]);
    titleRow.height = 30;
    const titleCell = titleRow.getCell(1);
    titleCell.font = { 
      size: 16, 
      bold: true, 
      color: { argb: '1F2937' },
      name: isRtl ? 'Arial' : 'Calibri'
    };
    titleCell.alignment = { 
      horizontal: 'center', 
      vertical: 'middle',
      wrapText: true 
    };
    titleCell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'DBEAFE' }
    };

    // التاريخ
    const dateRow = worksheet.addRow([
      isRtl ? `التاريخ: ${new Date().toLocaleDateString('ar-SA')}` : `Date: ${new Date().toLocaleDateString('en-US')}`
    ]);
    dateRow.height = 24;
    const dateCell = dateRow.getCell(1);
    dateCell.font = { 
      size: 10, 
      color: { argb: '6B7280' },
      name: isRtl ? 'Arial' : 'Calibri'
    };
    dateCell.alignment = { 
      horizontal: 'center', 
      vertical: 'middle',
      wrapText: true 
    };

    worksheet.addRow([]);

    // دمج الخلايا للعنوان والتاريخ
    const totalColumns = headers.length;
    worksheet.mergeCells(`A1:${String.fromCharCode(65 + totalColumns - 1)}1`);
    worksheet.mergeCells(`A2:${String.fromCharCode(65 + totalColumns - 1)}2`);
    
    // الهيدرات
    const headerRow = worksheet.addRow(headers);
    headerRow.height = 28;
    headerRow.eachCell((cell, colNumber) => {
      cell.font = { 
        bold: true, 
        size: 11, 
        color: { argb: 'FFFFFF' },
        name: isRtl ? 'Arial' : 'Calibri'
      };
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: '3B82F6' }
      };
      cell.alignment = { 
        horizontal: 'center', 
        vertical: 'middle',
        wrapText: true 
      };
      cell.border = {
        top: { style: 'thin', color: { argb: '1D4ED8' } },
        left: { style: 'thin', color: { argb: '1D4ED8' } },
        bottom: { style: 'thin', color: { argb: '1D4ED8' } },
        right: { style: 'thin', color: { argb: '1D4ED8' } }
      };
    });

    // البيانات
    body.forEach((rowData, rowIndex) => {
      const row = worksheet.addRow(rowData);
      row.height = 24;
      
      row.eachCell((cell, colNumber) => {
        cell.alignment = { 
          horizontal: 'center', 
          vertical: 'middle',
          wrapText: true 
        };
        cell.border = {
          top: { style: 'thin', color: { argb: 'E5E7EB' } },
          left: { style: 'thin', color: { argb: 'E5E7EB' } },
          bottom: { style: 'thin', color: { argb: 'E5E7EB' } },
          right: { style: 'thin', color: { argb: 'E5E7EB' } }
        };
        
        if (rowIndex % 2 === 0) {
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'F9FAFB' }
          };
        }
        
        // تنسيق الأرقام
        if (typeof rowData[colNumber - 1] === 'number') {
          cell.numFmt = '#,##0';
          if (rowIndex === body.length - 1) {
            cell.font = { 
              bold: true,
              color: { argb: 'D97706' },
              name: isRtl ? 'Arial' : 'Calibri'
            };
          }
        }
      });
    });

    // ضبط أعمدة الجدول
    const columnWidths = headers.map(() => 18);
    worksheet.columns = columnWidths.map(width => ({ width }));

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    saveAs(blob, fileName);

    toast.update(toastId, {
      render: isRtl ? 'تم التصدير إلى Excel بنجاح!' : 'Exported to Excel successfully!',
      type: 'success',
      autoClose: 3000,
    });
  } catch (error) {
    console.error('Excel Export Error:', error);
    toast.update(toastId, {
      render: isRtl ? 'فشل تصدير Excel. حاول مرة أخرى.' : 'Excel export failed. Please try again.',
      type: 'error',
      autoClose: 4000,
    });
  }
};

// ==============================
// === تصدير تقرير الشيفات ===
// ==============================
const exportChefsReport = async (
  chefsData: ChefProductionData[],
  isRtl: boolean,
  periodLabel: string,
  showCompleted: boolean,
  fileType: 'pdf' | 'excel'
): Promise<void> => {
  if (chefsData.length === 0) {
    toast.warning(isRtl ? 'لا توجد بيانات للتصدير' : 'No data to export', {
      position: isRtl ? 'top-left' : 'top-right'
    });
    return;
  }

  const title = isRtl 
    ? `تقرير إنتاج الشيفات - ${periodLabel}`
    : `Chefs Production Report - ${periodLabel}`;

  // إنشاء الهيدرات
  const headers = [
    '#',
    isRtl ? 'اسم الشيف' : 'Chef Name',
    isRtl ? 'البريد الإلكتروني' : 'Email',
    isRtl ? 'عدد المنتجات' : 'Products Count',
    isRtl ? 'المطلوب' : 'Assigned'
  ];

  if (showCompleted) {
    headers.push(isRtl ? 'المكتمل' : 'Completed');
    headers.push(isRtl ? 'نسبة الإنجاز' : 'Completion Rate');
  }

  headers.push(isRtl ? 'الحالة' : 'Status');

  // إنشاء البيانات
  const body = chefsData.map((chef, index) => {
    const row = [
      index + 1,
      fixArabicText(chef.name),
      chef.email || '-',
      Object.keys(chef.products || {}).length,
      chef.totalAssignedQuantity || 0
    ];

    if (showCompleted) {
      row.push(chef.totalCompletedQuantity || 0);
      const completionRate = chef.totalAssignedQuantity > 0 
        ? (chef.totalCompletedQuantity / chef.totalAssignedQuantity) * 100 
        : 0;
      row.push(formatPercentage(completionRate, isRtl));
    }

    row.push(chef.isActive ? (isRtl ? 'نشط' : 'Active') : (isRtl ? 'غير نشط' : 'Inactive'));

    return row;
  });

  // إضافة صف الإجمالي
  const totalRow = [
    '',
    isRtl ? 'الإجمالي' : 'Total',
    '',
    chefsData.reduce((sum, chef) => sum + Object.keys(chef.products || {}).length, 0),
    chefsData.reduce((sum, chef) => sum + (chef.totalAssignedQuantity || 0), 0)
  ];

  if (showCompleted) {
    const totalCompleted = chefsData.reduce((sum, chef) => sum + (chef.totalCompletedQuantity || 0), 0);
    const totalAssigned = chefsData.reduce((sum, chef) => sum + (chef.totalAssignedQuantity || 0), 0);
    const totalCompletionRate = totalAssigned > 0 ? (totalCompleted / totalAssigned) * 100 : 0;
    
    totalRow.push(totalCompleted);
    totalRow.push(formatPercentage(totalCompletionRate, isRtl));
  }

  totalRow.push('');
  body.push(totalRow);

  const fileName = `chefs_report_${new Date().toISOString().split('T')[0]}.${fileType}`;

  if (fileType === 'pdf') {
    await exportToPDF(title, headers, body, isRtl, true, fileName);
  } else {
    await exportToExcel(title, headers, body, isRtl, fileName);
  }
};

// ==============================
// === COMPONENTS ===
// ==============================

const MultiSelectDropdown: React.FC<{
  value: string[];
  onChange: (values: string[]) => void;
  options: { value: string; label: string }[];
  label?: string;
  isRtl: boolean;
  placeholder?: string;
}> = ({ value, onChange, options, label, isRtl, placeholder }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const toggleOption = useCallback((optionValue: string) => {
    if (optionValue === 'all') {
      onChange(['all']);
    } else {
      const newValues = value.filter(v => v !== 'all');
      if (newValues.includes(optionValue)) {
        const filtered = newValues.filter(v => v !== optionValue);
        onChange(filtered.length === 0 ? ['all'] : filtered);
      } else {
        onChange([...newValues, optionValue]);
      }
    }
  }, [value, onChange]);

  const allSelected = value.includes('all') || value.length === 0;
  const count = value.filter(v => v !== 'all').length;
  
  let displayLabel = '';
  if (allSelected) {
    displayLabel = isRtl ? 'الكل' : 'All';
  } else if (count === 0) {
    displayLabel = placeholder || (isRtl ? 'اختر...' : 'Select...');
  } else if (count === 1) {
    const selectedOption = options.find(opt => opt.value === value[0]);
    displayLabel = selectedOption?.label || (isRtl ? 'محدد' : 'Selected');
  } else {
    displayLabel = isRtl 
      ? `${count} ${count === 2 ? 'محددين' : 'محدد'}`
      : `${count} selected`;
  }

  return (
    <div className="w-full">
      {label && (
        <label className="block text-xs font-medium text-gray-700 mb-1.5" dir={isRtl ? 'rtl' : 'ltr'}>
          {label}
        </label>
      )}
      <div className="relative" ref={dropdownRef}>
        <button
          dir={isRtl ? 'rtl' : 'ltr'}
          onClick={() => setIsOpen(!isOpen)}
          className={`w-full py-2 px-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-300 focus:border-amber-400 bg-white shadow-sm text-sm font-medium text-gray-700 flex justify-between items-center ${isRtl ? 'text-right' : 'text-left'} transition-all duration-200`}
          type="button"
        >
          <span className="truncate flex-1">{displayLabel}</span>
          <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform duration-200 flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
        </button>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className={`absolute z-50 w-full mt-1.5 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto font-medium ${isRtl ? 'text-right' : 'text-left'}`}
          >
            {options.map(opt => {
              const isSelected = allSelected ? opt.value === 'all' : value.includes(opt.value);
              return (
                <div 
                  key={opt.value} 
                  className="flex items-center justify-between px-3 py-2 hover:bg-amber-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors"
                  onClick={() => toggleOption(opt.value)}
                >
                  <span className="text-sm text-gray-700 flex-1" dir={isRtl ? 'rtl' : 'ltr'}>
                    {opt.label}
                  </span>
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => {}}
                    className="h-4 w-4 text-amber-500 rounded border-gray-300 focus:ring-amber-300 cursor-pointer"
                    readOnly
                  />
                </div>
              );
            })}
          </motion.div>
        )}
      </div>
    </div>
  );
};

const CompactExportDropdown: React.FC<{
  onExportPDF: (showCompleted: boolean) => void;
  onExportExcel: (showCompleted: boolean) => void;
  disabled: boolean;
  isRtl: boolean;
  showCompleted: boolean;
  onToggleShowCompleted: () => void;
}> = ({ onExportPDF, onExportExcel, disabled, isRtl, showCompleted, onToggleShowCompleted }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  return (
    <div className="flex items-center gap-2">
      <button
        onClick={onToggleShowCompleted}
        className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-all ${
          showCompleted
            ? 'bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100'
            : 'bg-amber-50 border-amber-200 text-amber-700 hover:bg-amber-100'
        }`}
        type="button"
      >
        <CheckCircle className="w-4 h-4" />
        <span className="hidden sm:inline">
          {showCompleted ? (isRtl ? 'المكتمل' : 'Completed') : (isRtl ? 'بدون مكتمل' : 'Without')}
        </span>
      </button>
      
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          disabled={disabled}
          className="flex items-center gap-2 px-3 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-sm font-medium transition-all disabled:opacity-60 disabled:cursor-not-allowed"
          type="button"
        >
          <Download className="w-4 h-4" />
          <span className="hidden sm:inline">{isRtl ? 'تصدير' : 'Export'}</span>
        </button>
        
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.2 }}
            className={`absolute z-50 mt-1.5 ${isRtl ? 'left-0' : 'right-0'} w-36 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden`}
          >
            <button
              onClick={() => {
                onExportExcel(showCompleted);
                setIsOpen(false);
              }}
              disabled={disabled}
              className="w-full px-3 py-2 text-sm text-gray-700 hover:bg-amber-50 hover:text-amber-700 transition-colors border-b border-gray-100 flex items-center gap-2"
              type="button"
            >
              <FileSpreadsheet className="w-4 h-4" />
              <span>Excel</span>
            </button>
            <button
              onClick={() => {
                onExportPDF(showCompleted);
                setIsOpen(false);
              }}
              disabled={disabled}
              className="w-full px-3 py-2 text-sm text-gray-700 hover:bg-amber-50 hover:text-amber-700 transition-colors flex items-center gap-2"
              type="button"
            >
              <FileText className="w-4 h-4" />
              <span>PDF</span>
            </button>
          </motion.div>
        )}
      </div>
    </div>
  );
};

const Button: React.FC<{
  variant: 'primary' | 'outline' | 'success' | 'amber';
  onClick?: () => void;
  disabled?: boolean;
  children: React.ReactNode;
  icon?: React.ReactNode;
  size?: 'sm' | 'md';
}> = ({ variant, onClick, disabled, children, icon, size = 'md' }) => {
  const sizeClasses = {
    sm: 'px-3 py-1.5 text-xs',
    md: 'px-4 py-2 text-sm'
  };
  
  const variantClasses = {
    amber: 'bg-amber-500 hover:bg-amber-600 text-white shadow-sm hover:shadow',
    primary: 'bg-gray-800 hover:bg-gray-900 text-white shadow-sm hover:shadow',
    success: 'bg-emerald-500 hover:bg-emerald-600 text-white shadow-sm hover:shadow',
    outline: 'border border-gray-200 hover:bg-gray-50 text-gray-700 hover:border-gray-300'
  };
  
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`flex items-center justify-center gap-2 rounded-lg font-medium transition-all ${sizeClasses[size]} ${variantClasses[variant]} ${disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
      type="button"
    >
      {icon && <span className="flex-shrink-0">{icon}</span>}
      <span className="whitespace-nowrap">{children}</span>
    </button>
  );
};

const SearchInput: React.FC<{
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder: string;
  isRtl: boolean;
}> = ({ value, onChange, placeholder, isRtl }) => {
  return (
    <div className="relative w-full">
      <input
        type="text"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className={`w-full ${isRtl ? 'pl-10 pr-3' : 'pr-10 pl-3'} py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-300 focus:border-amber-400 bg-white text-sm ${isRtl ? 'text-right' : 'text-left'} transition-all duration-200`}
        dir={isRtl ? 'rtl' : 'ltr'}
      />
      {value ? (
        <button
          onClick={() => onChange({ target: { value: '' } } as any)}
          className={`absolute ${isRtl ? 'left-3' : 'right-3'} top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors`}
          type="button"
        >
          <X className="w-4 h-4" />
        </button>
      ) : (
        <Search className={`absolute ${isRtl ? 'left-3' : 'right-3'} top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4`} />
      )}
    </div>
  );
};

const Dropdown: React.FC<{
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  label?: string;
  icon?: React.ReactNode;
  isRtl: boolean;
}> = ({ value, onChange, options, label, icon, isRtl }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const selectedOption = options.find(opt => opt.value === value) || options[0];

  return (
    <div className="w-full">
      {label && (
        <label className="block text-xs font-medium text-gray-700 mb-1.5" dir={isRtl ? 'rtl' : 'ltr'}>
          {label}
        </label>
      )}
      <div className="relative" ref={dropdownRef}>
        <button
          dir={isRtl ? 'rtl' : 'ltr'}
          onClick={() => setIsOpen(!isOpen)}
          className={`w-full py-2 px-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-300 focus:border-amber-400 bg-white shadow-sm text-sm font-medium text-gray-700 flex justify-between items-center ${isRtl ? 'text-right' : 'text-left'} transition-all duration-200`}
          type="button"
        >
          <div className="flex items-center gap-2 flex-1 truncate" dir={isRtl ? 'rtl' : 'ltr'}>
            {icon && <span className="flex-shrink-0">{icon}</span>}
            <span className="truncate">{selectedOption?.label || ''}</span>
          </div>
          <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform duration-200 flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
        </button>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className={`absolute z-50 w-full mt-1.5 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto font-medium ${isRtl ? 'text-right' : 'text-left'}`}
          >
            {options.map(opt => (
              <button
                key={opt.value}
                onClick={() => {
                  onChange(opt.value);
                  setIsOpen(false);
                }}
                className={`w-full px-3 py-2 text-sm ${isRtl ? 'text-right' : 'text-left'} text-gray-700 hover:bg-amber-50 hover:text-amber-700 transition-colors border-b border-gray-100 last:border-b-0`}
                type="button"
                dir={isRtl ? 'rtl' : 'ltr'}
              >
                {opt.label}
              </button>
            ))}
          </motion.div>
        )}
      </div>
    </div>
  );
};

const StatsCard: React.FC<{
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color: 'gray' | 'emerald' | 'blue' | 'amber' | 'green';
  loading?: boolean;
  isRtl: boolean;
  trend?: number;
}> = ({ title, value, icon, color, loading, isRtl, trend }) => {
  const colors = {
    gray: 'bg-gray-50 text-gray-700 border-gray-200',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    green: 'bg-green-50 text-green-700 border-green-200'
  };

  return (
    <div className={`p-4 rounded-xl border ${colors[color]} transition-all hover:shadow-sm`} dir={isRtl ? 'rtl' : 'ltr'}>
      <div className="flex items-center justify-between mb-3">
        <div className="p-2 rounded-lg bg-white">
          {icon}
        </div>
        {trend !== undefined && (
          <div className={`flex items-center gap-1 text-sm ${trend > 0 ? 'text-emerald-600' : trend < 0 ? 'text-red-600' : 'text-gray-500'}`}>
            <TrendingUp className={`w-4 h-4 ${trend < 0 ? 'rotate-180' : ''}`} />
            <span>{trend > 0 ? '+' : ''}{trend}%</span>
          </div>
        )}
      </div>
      <div className="mb-2">
        <h3 className="text-sm font-medium opacity-80">{title}</h3>
      </div>
      <div className="text-xl font-bold">
        {loading ? (
          <div className="h-7 w-24 bg-gray-200 rounded animate-pulse"></div>
        ) : (
          value
        )}
      </div>
    </div>
  );
};

// ==============================
// === MAIN COMPONENT ===
// ==============================
const ProductionReport: React.FC = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const isRtl = language === 'ar';
  const isChef = user?.role === 'chef';
  const isAdminOrProductionManager = user?.role === 'admin' || user?.role === 'production';
  
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [chefsData, setChefsData] = useState<ChefProductionData[]>([]);
  const [chefsList, setChefsList] = useState<Chef[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [expandedChefs, setExpandedChefs] = useState<number>(10);
  const [showCompleted, setShowCompleted] = useState(true);
  
  const [period, setPeriod] = useState('month');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('code_asc');
  const [selectedChefsMulti, setSelectedChefsMulti] = useState<string[]>(['all']);
  
  const periodOptions = useMemo(() => [
    { value: 'today', label: isRtl ? 'اليوم' : 'Today' },
    { value: 'yesterday', label: isRtl ? 'أمس' : 'Yesterday' },
    { value: 'week', label: isRtl ? 'آخر 7 أيام' : 'Last 7 Days' },
    { value: 'month', label: isRtl ? 'آخر 30 يوم' : 'Last 30 Days' },
  ], [isRtl]);
  
  const sortOptions = useMemo(() => [
    { value: 'code_asc', label: isRtl ? 'الكود (أ-ي)' : 'Code (A-Z)' },
    { value: 'code_desc', label: isRtl ? 'الكود (ي-أ)' : 'Code (Z-A)' },
    { value: 'name_asc', label: isRtl ? 'اسم المنتج (أ-ي)' : 'Product Name (A-Z)' },
    { value: 'name_desc', label: isRtl ? 'اسم المنتج (ي-أ)' : 'Product Name (Z-A)' },
    { value: 'quantity_desc', label: isRtl ? 'الكمية (الأكثر أولاً)' : 'Quantity (High to Low)' },
  ], [isRtl]);

  const chefMultiSelectOptions = useMemo(() => {
    const baseOptions = chefsData.map(chef => ({
      value: chef.id,
      label: fixArabicText(chef.name)
    })).sort((a, b) => a.label.localeCompare(b.label));
    return [{ value: 'all', label: isRtl ? 'جميع الشيفات' : 'All Chefs' }, ...baseOptions];
  }, [chefsData, isRtl]);

  const allProducts = useMemo(() => {
    const productMap = new Map<string, ProductData>();
    
    chefsData.forEach(chef => {
      if (chef.products && typeof chef.products === 'object') {
        Object.entries(chef.products).forEach(([productId, product]) => {
          if (product && typeof product === 'object' && product.id) {
            const enhancedProduct = enhanceProductWithBranchData({
              ...product,
            }, branches);
            
            productMap.set(product.id, enhancedProduct);
          }
        });
      }
    });
    
    const productsArray = Array.from(productMap.values());
    
    return productsArray.sort((a, b) => {
      switch (sortBy) {
        case 'code_asc':
          return (a.code || '').localeCompare(b.code || '');
        case 'code_desc':
          return (b.code || '').localeCompare(a.code || '');
        case 'name_asc':
          return fixArabicText(a.name).localeCompare(fixArabicText(b.name));
        case 'name_desc':
          return fixArabicText(b.name).localeCompare(fixArabicText(a.name));
        case 'quantity_desc':
          const totalA = chefsData.reduce((sum, chef) => sum + (chef.products?.[a.id]?.totalAssigned || 0), 0);
          const totalB = chefsData.reduce((sum, chef) => sum + (chef.products?.[b.id]?.totalAssigned || 0), 0);
          return totalB - totalA;
        default:
          return fixArabicText(a.name).localeCompare(fixArabicText(b.name));
      }
    });
  }, [chefsData, sortBy, branches]);

  const filteredChefsData = useMemo(() => {
    let filtered = chefsData;
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(chef => 
        chef.name?.toLowerCase().includes(query) ||
        chef.email?.toLowerCase().includes(query)
      );
    }
    
    if (selectedChefsMulti.length > 0 && !selectedChefsMulti.includes('all')) {
      filtered = filtered.filter(chef => selectedChefsMulti.includes(chef.id));
    }
    
    filtered = [...filtered].sort((a, b) => 
      fixArabicText(a.name || '').localeCompare(fixArabicText(b.name || ''))
    );
    
    return filtered;
  }, [chefsData, searchQuery, selectedChefsMulti]);

  const displayedChefs = useMemo(() => {
    return filteredChefsData.slice(0, expandedChefs);
  }, [filteredChefsData, expandedChefs]);

  const getProductValuesForChef = useCallback((chef: ChefProductionData, product: ProductData) => {
    if (!chef.products || !chef.products[product.id]) {
      return { assigned: 0, completed: 0 };
    }
    
    const chefProduct = chef.products[product.id];
    return {
      assigned: chefProduct.totalAssigned || 0,
      completed: chefProduct.totalCompleted || 0
    };
  }, []);

  const calculateChefTotals = useCallback((chef: ChefProductionData) => {
    let assignedTotal = 0;
    let completedTotal = 0;
    
    allProducts.forEach(product => {
      const values = getProductValuesForChef(chef, product);
      assignedTotal += values.assigned;
      completedTotal += values.completed;
    });
    
    return { assignedTotal, completedTotal };
  }, [allProducts, getProductValuesForChef]);

  const calculateProductTotals = useCallback((product: ProductData) => {
    let assignedTotal = 0;
    let completedTotal = 0;
    
    displayedChefs.forEach(chef => {
      const values = getProductValuesForChef(chef, product);
      assignedTotal += values.assigned;
      completedTotal += values.completed;
    });
    
    return { assignedTotal, completedTotal };
  }, [displayedChefs, getProductValuesForChef]);

  const getDateRange = useCallback(() => {
    const { start, end } = calculatePeriodDates(period);
    return {
      startDate: format(start, 'yyyy-MM-dd'),
      endDate: format(end, 'yyyy-MM-dd')
    };
  }, [period]);

  const fetchChefsList = useCallback(async () => {
    try {
      const cacheKey = 'chefs_list';
      const cached = globalCache.get(cacheKey);
      
      if (cached) {
        setChefsList(cached);
        return;
      }
      
      const response = await chefsAPI.getAll();
      globalCache.set(cacheKey, response, 5 * 60 * 1000);
      setChefsList(response);
    } catch (error) {
      toast.error(isRtl ? 'فشل في تحميل قائمة الشيفات' : 'Failed to load chefs list', {
        position: isRtl ? 'top-left' : 'top-right'
      });
    }
  }, [isRtl]);

  const fetchBranches = useCallback(async () => {
    try {
      const cacheKey = 'branches_list';
      const cached = globalCache.get(cacheKey);
      
      if (cached) {
        setBranches(cached);
        return;
      }
      
      const response = await branchesAPI.getBranches();
      const branchesData = response.data || [];
      
      // إضافة ألوان للفروع
      const coloredBranches = branchesData.map((branch: Branch, index: number) => ({
        ...branch,
        color: getRandomColor(index)
      }));
      
      globalCache.set(cacheKey, coloredBranches, 10 * 60 * 1000);
      setBranches(coloredBranches);
    } catch (error) {
      console.warn('Failed to fetch branches:', error);
      setBranches([]);
    }
  }, []);

  const fetchProductionData = useCallback(async (forceRefresh = false) => {
    if (!forceRefresh) setLoading(true);
    else setRefreshing(true);
    
    try {
      const range = getDateRange();
      const cacheKey = `production_data_${range.startDate}_${range.endDate}`;
      
      if (!forceRefresh) {
        const cached = globalCache.get(cacheKey);
        if (cached) {
          setChefsData(cached);
          setLoading(false);
          return;
        }
      }
      
      const params: any = {
        startDate: range.startDate,
        endDate: range.endDate,
        groupBy: 'chef',
        lang: language,
        isRtl: isRtl ? 'true' : 'false',
        includeBranches: 'true'
      };
      
      const response = await reportsAPI.getChefsProductionSummary(params);
      
      let apiData: ChefProductionData[] = [];
      
      if (response.data?.success) {
        apiData = response.data.data || [];
      } else if (Array.isArray(response.data)) {
        apiData = response.data;
      } else if (Array.isArray(response)) {
        apiData = response;
      }
      
      // تحسين بيانات الفروع
      const enhancedData = apiData.map(chef => ({
        ...chef,
        branchSummary: chef.branchSummary || {}
      }));
      
      globalCache.set(cacheKey, enhancedData, 2 * 60 * 1000);
      setChefsData(enhancedData);
      
    } catch (error: any) {
      console.error('Error fetching production data:', error);
      toast.error(error.message || (isRtl ? 'فشل في تحميل بيانات الإنتاج' : 'Failed to load production data'), {
        position: isRtl ? 'top-left' : 'top-right'
      });
      setChefsData([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [getDateRange, isRtl, language]);

  useEffect(() => {
    let mounted = true;
    
    const initialize = async () => {
      try {
        await Promise.all([
          fetchChefsList(),
          fetchBranches(),
          fetchProductionData()
        ]);
      } catch (error) {
        console.error('Initialization error:', error);
      }
    };
    
    initialize();
    
    return () => {
      mounted = false;
      globalCache.destroy();
    };
  }, []);

  const debouncedFetch = useRef<NodeJS.Timeout>();
  
  useEffect(() => {
    if (debouncedFetch.current) {
      clearTimeout(debouncedFetch.current);
    }
    
    debouncedFetch.current = setTimeout(() => {
      fetchProductionData();
    }, 500);
    
    return () => {
      if (debouncedFetch.current) {
        clearTimeout(debouncedFetch.current);
      }
    };
  }, [period, fetchProductionData]);

  const toggleExpandChefs = useCallback(() => {
    setExpandedChefs(expandedChefs === filteredChefsData.length ? 10 : filteredChefsData.length);
  }, [expandedChefs, filteredChefsData.length]);

  const periodLabel = useMemo(() => {
    const locale = isRtl ? arSA : enUS;
    
    const { start, end } = calculatePeriodDates(period);
    if (isSameDay(start, end)) {
      return isRtl
        ? `${format(start, 'EEEE d/M/yyyy', { locale })}`
        : `${format(start, 'EEEE d/M/yyyy', { locale })}`;
    }
    return isRtl
      ? `${format(start, 'd/M/yyyy', { locale })} - ${format(end, 'd/M/yyyy', { locale })}`
      : `${format(start, 'd/M/yyyy', { locale })} - ${format(end, 'd/M/yyyy', { locale })}`;
  }, [period, isRtl]);
  
  const handleExportPDF = useCallback((showCompletedInExport: boolean = showCompleted) => {
    exportChefsReport(
      filteredChefsData,
      isRtl,
      periodLabel,
      showCompletedInExport,
      'pdf'
    );
  }, [filteredChefsData, isRtl, periodLabel, showCompleted]);
  
  const handleExportExcel = useCallback((showCompletedInExport: boolean = showCompleted) => {
    exportChefsReport(
      filteredChefsData,
      isRtl,
      periodLabel,
      showCompletedInExport,
      'excel'
    );
  }, [filteredChefsData, isRtl, periodLabel, showCompleted]);
  
  const handleRefresh = useCallback(() => {
    globalCache.delete(`production_data_${getDateRange().startDate}_${getDateRange().endDate}`);
    fetchProductionData(true);
    toast.success(isRtl ? 'تم تحديث البيانات' : 'Data refreshed successfully', {
      position: isRtl ? 'top-left' : 'top-right'
    });
  }, [fetchProductionData, isRtl, getDateRange]);

  const toggleShowCompleted = useCallback(() => {
    setShowCompleted(prev => !prev);
  }, []);

  const renderChefsTable = () => {
    if (isChef) {
      return (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200" dir={isRtl ? 'rtl' : 'ltr'}>
              <thead>
                <tr className="bg-amber-50">
                  <th className="px-4 py-3 text-xs font-medium text-gray-700 text-center whitespace-nowrap border-l border-amber-200 min-w-[70px] sticky left-0 bg-amber-50 z-10">
                    #
                  </th>
                  <th className="px-4 py-3 text-xs font-medium text-gray-700 text-center whitespace-nowrap border-l border-amber-200 min-w-[90px] sticky left-[70px] bg-amber-50 z-10">
                    {isRtl ? 'الكود' : 'Code'}
                  </th>
                  <th className="px-4 py-3 text-xs font-medium text-gray-700 text-center whitespace-nowrap border-l border-amber-200 min-w-[200px] sticky left-[160px] bg-amber-50 z-10">
                    {isRtl ? 'المنتج' : 'Product'}
                  </th>
                  
                  <th colSpan={showCompleted ? 2 : 1} className="px-4 py-3 text-xs font-medium text-gray-700 text-center whitespace-nowrap min-w-[140px] bg-amber-100 sticky right-0 z-10">
                    <div className="flex flex-col">
                      <span>{isRtl ? 'الإجمالي' : 'Total'}</span>
                      <div className="flex w-full border-t border-amber-200 mt-1">
                        <div className={`text-center py-1 text-[10px] font-medium bg-amber-100 text-amber-800 ${showCompleted ? 'w-1/2' : 'w-full'}`}>
                          {isRtl ? 'مطلوب' : 'A'}
                        </div>
                        {showCompleted && (
                          <div className="w-1/2 text-center py-1 text-[10px] font-medium bg-emerald-100 text-emerald-800">
                            {isRtl ? 'مكتمل' : 'C'}
                          </div>
                        )}
                      </div>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {allProducts.map((product, productIndex) => {
                  const productTotals = calculateProductTotals(product);
                  
                  return (
                    <tr
                      key={product.id}
                      className={`transition-colors ${productIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'} hover:bg-gray-50`}
                    >
                      <td className="px-4 py-3 text-gray-800 text-center border-l border-gray-200 sticky left-0 bg-white">
                        <span className="font-medium text-gray-700">
                          {productIndex + 1}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-800 text-center border-l border-gray-200 sticky left-[70px] bg-white">
                        <span className="font-mono bg-gray-100 px-3 py-1.5 rounded text-xs font-medium text-gray-700">
                          {product.code || '-'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-800 text-center border-l border-gray-200 sticky left-[160px] bg-white">
                        <span className="font-medium text-gray-900 text-sm">
                          {fixArabicText(product.name)}
                        </span>
                      </td>
                      
                      <td className={`px-4 py-3 text-center border-l border-gray-200 ${getQuantityBgColor(productTotals.assignedTotal, false)} sticky ${showCompleted ? 'right-[70px]' : 'right-0'} bg-amber-50`}>
                        <span className={`font-bold text-sm ${getQuantityColor(productTotals.assignedTotal, false)}`}>
                          {formatNumber(productTotals.assignedTotal, isRtl)}
                        </span>
                      </td>
                      {showCompleted && (
                        <td className={`px-4 py-3 text-center ${getQuantityBgColor(productTotals.completedTotal, true)} sticky right-0 bg-emerald-50`}>
                          <span className={`font-bold text-sm ${getQuantityColor(productTotals.completedTotal, true)}`}>
                            {formatNumber(productTotals.completedTotal, isRtl)}
                          </span>
                        </td>
                      )}
                    </tr>
                  );
                })}
                
                <tr className="bg-amber-100 font-bold border-t-2 border-amber-300">
                  <td className="px-4 py-3 text-gray-800 text-center border-l border-amber-300 sticky left-0 bg-amber-100">
                    {isRtl ? 'الإجمالي' : 'Total'}
                  </td>
                  <td className="px-4 py-3 text-gray-800 text-center border-l border-amber-300 sticky left-[70px] bg-amber-100">
                    -
                  </td>
                  <td className="px-4 py-3 text-gray-800 text-center border-l border-amber-300 sticky left-[160px] bg-amber-100">
                    -
                  </td>
                  
                  <td className={`px-4 py-3 text-center border-l border-amber-300 bg-amber-200 ${showCompleted ? 'sticky right-[70px]' : 'sticky right-0'}`}>
                    <span className="text-amber-800 font-bold text-sm">
                      {formatNumber(
                        displayedChefs.reduce((sum, chef) => sum + calculateChefTotals(chef).assignedTotal, 0),
                        isRtl
                      )}
                    </span>
                  </td>
                  {showCompleted && (
                    <td className="px-4 py-3 text-center bg-emerald-200 sticky right-0">
                      <span className="text-emerald-800 font-bold text-sm">
                        {formatNumber(
                          displayedChefs.reduce((sum, chef) => sum + calculateChefTotals(chef).completedTotal, 0),
                          isRtl
                        )}
                      </span>
                    </td>
                  )}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      );
    } else {
      // هنا الكود المعدل للعرض العادي (غير الشيف)
      // سيتم عرض عمودين منفصلين: المطلوب والمكتمل لكل شيف
      return (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200" dir={isRtl ? 'rtl' : 'ltr'}>
              <thead>
                <tr className="bg-amber-50">
                  <th className="px-4 py-3 text-xs font-medium text-gray-700 text-center whitespace-nowrap border-l border-amber-200 min-w-[60px] sticky left-0 bg-amber-50 z-10">
                    #
                  </th>
                  <th className="px-4 py-3 text-xs font-medium text-gray-700 text-center whitespace-nowrap border-l border-amber-200 min-w-[80px] sticky left-[60px] bg-amber-50 z-10">
                    {isRtl ? 'الكود' : 'Code'}
                  </th>
                  <th className="px-4 py-3 text-xs font-medium text-gray-700 text-center whitespace-nowrap border-l border-amber-200 min-w-[180px] sticky left-[140px] bg-amber-50 z-10">
                    {isRtl ? 'المنتج' : 'Product'}
                  </th>
                  
                  {displayedChefs.map((chef, chefIndex) => (
                    <React.Fragment key={chef.id}>
                      {/* عمود المطلوب للشيف */}
                      <th
                        className={`px-4 py-3 text-xs font-medium text-gray-700 text-center whitespace-nowrap border-l border-amber-200 min-w-[80px] ${chefIndex === 0 ? '' : ''}`}
                      >
                        <div className="flex flex-col items-center">
                          <span className="text-xs truncate max-w-[70px] mb-1">
                            {fixArabicText(chef.name)}
                          </span>
                          <div className="text-[10px] text-amber-700 bg-amber-100 px-1 py-0.5 rounded">
                            {isRtl ? 'مطلوب' : 'Assigned'}
                          </div>
                        </div>
                      </th>
                      
                      {/* عمود المكتمل للشيف - يظهر فقط إذا كان showCompleted = true */}
                      {showCompleted && (
                        <th
                          className={`px-4 py-3 text-xs font-medium text-gray-700 text-center whitespace-nowrap border-l border-amber-200 min-w-[80px] ${chefIndex === displayedChefs.length - 1 ? '' : ''}`}
                        >
                          <div className="flex flex-col items-center">
                            <span className="text-xs truncate max-w-[70px] mb-1">
                              {fixArabicText(chef.name)}
                            </span>
                            <div className="text-[10px] text-emerald-700 bg-emerald-100 px-1 py-0.5 rounded">
                              {isRtl ? 'مكتمل' : 'Completed'}
                            </div>
                          </div>
                        </th>
                      )}
                    </React.Fragment>
                  ))}
                  
                  <th className="px-4 py-3 text-xs font-medium text-gray-700 text-center whitespace-nowrap min-w-[100px] bg-amber-100 sticky right-0 z-10">
                    {isRtl ? 'الإجمالي' : 'Total'}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {allProducts.map((product, productIndex) => {
                  let rowTotalAssigned = 0;
                  let rowTotalCompleted = 0;
                  
                  return (
                    <tr
                      key={product.id}
                      className={`transition-colors ${productIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'} hover:bg-gray-50`}
                    >
                      <td className="px-4 py-3 text-gray-800 text-center border-l border-gray-200 sticky left-0 bg-white">
                        <span className="font-medium text-gray-700">
                          {productIndex + 1}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-800 text-center border-l border-gray-200 sticky left-[60px] bg-white">
                        <span className="font-mono bg-gray-100 px-2 py-1 rounded text-xs font-medium text-gray-700">
                          {product.code || '-'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-800 text-center border-l border-gray-200 sticky left-[140px] bg-white">
                        <span className="font-medium text-gray-900 text-sm">
                          {fixArabicText(product.name)}
                        </span>
                      </td>
                      
                      {displayedChefs.map((chef) => {
                        const values = getProductValuesForChef(chef, product);
                        rowTotalAssigned += values.assigned;
                        rowTotalCompleted += values.completed;
                        
                        return (
                          <React.Fragment key={`${product.id}-${chef.id}`}>
                            {/* خلية المطلوب */}
                            <td
                              className={`px-4 py-3 text-center border-l border-gray-200 ${getQuantityBgColor(values.assigned, false)}`}
                            >
                              <span className={`font-medium text-sm ${getQuantityColor(values.assigned, false)}`}>
                                {formatNumber(values.assigned, isRtl)}
                              </span>
                            </td>
                            
                            {/* خلية المكتمل - تظهر فقط إذا كان showCompleted = true */}
                            {showCompleted && (
                              <td
                                className={`px-4 py-3 text-center border-l border-gray-200 ${getQuantityBgColor(values.completed, true)}`}
                              >
                                <span className={`font-medium text-sm ${getQuantityColor(values.completed, true)}`}>
                                  {formatNumber(values.completed, isRtl)}
                                </span>
                              </td>
                            )}
                          </React.Fragment>
                        );
                      })}
                      
                      {/* إجمالي الصف */}
                      <td className={`px-4 py-3 text-center ${getQuantityBgColor(rowTotalAssigned, false)} sticky right-0 bg-amber-50`}>
                        <div className="space-y-1">
                          <div className={`font-bold text-sm ${getQuantityColor(rowTotalAssigned, false)}`}>
                            {formatNumber(rowTotalAssigned, isRtl)}
                          </div>
                          {showCompleted && rowTotalCompleted > 0 && (
                            <div className="text-xs text-emerald-600 font-medium">
                              ({formatNumber(rowTotalCompleted, isRtl)})
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                
                {/* صف الإجماليات */}
                <tr className="bg-amber-100 font-bold border-t-2 border-amber-300">
                  <td className="px-4 py-3 text-gray-800 text-center border-l border-amber-300 sticky left-0 bg-amber-100">
                    {isRtl ? 'الإجمالي' : 'Total'}
                  </td>
                  <td className="px-4 py-3 text-gray-800 text-center border-l border-amber-300 sticky left-[60px] bg-amber-100">
                    -
                  </td>
                  <td className="px-4 py-3 text-gray-800 text-center border-l border-amber-300 sticky left-[140px] bg-amber-100">
                    -
                  </td>
                  
                  {displayedChefs.map((chef) => {
                    const chefTotalAssigned = allProducts.reduce((sum, product) => {
                      const values = getProductValuesForChef(chef, product);
                      return sum + values.assigned;
                    }, 0);
                    
                    const chefTotalCompleted = allProducts.reduce((sum, product) => {
                      const values = getProductValuesForChef(chef, product);
                      return sum + values.completed;
                    }, 0);
                    
                    return (
                      <React.Fragment key={`total-${chef.id}`}>
                        {/* إجمالي المطلوب للشيف */}
                        <td className="px-4 py-3 text-center border-l border-amber-300 bg-amber-200">
                          <span className="text-amber-800 font-bold text-sm">
                            {formatNumber(chefTotalAssigned, isRtl)}
                          </span>
                        </td>
                        
                        {/* إجمالي المكتمل للشيف - يظهر فقط إذا كان showCompleted = true */}
                        {showCompleted && (
                          <td className="px-4 py-3 text-center border-l border-amber-300 bg-emerald-200">
                            <span className="text-emerald-800 font-bold text-sm">
                              {formatNumber(chefTotalCompleted, isRtl)}
                            </span>
                          </td>
                        )}
                      </React.Fragment>
                    );
                  })}
                  
                  {/* إجمالي العمود */}
                  <td className="px-4 py-3 text-center bg-amber-200 sticky right-0">
                    <div className="space-y-1">
                      <div className="text-amber-800 font-bold text-sm">
                        {formatNumber(
                          displayedChefs.reduce((sum, chef) => sum + allProducts.reduce((s, product) => {
                            const values = getProductValuesForChef(chef, product);
                            return s + values.assigned;
                          }, 0), 0),
                          isRtl
                        )}
                      </div>
                      {showCompleted && (
                        <div className="text-xs text-emerald-800 font-medium">
                          ({formatNumber(
                            displayedChefs.reduce((sum, chef) => sum + allProducts.reduce((s, product) => {
                              const values = getProductValuesForChef(chef, product);
                              return s + values.completed;
                            }, 0), 0),
                            isRtl
                          )})
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      );
    }
  };

  return (
    <div className="min-h-screen p-4 md:p-6 bg-gray-50">
      <div className="max-w-full mx-auto">
        <div className="mb-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900 flex items-center gap-2">
                <ChefHat className="w-6 h-6 text-amber-500" />
                {isRtl ? 'تقرير إنتاج الشيفات' : 'Chefs Production Report'}
              </h1>
              <p className="text-gray-600 mt-1">
                {isRtl ? 'عرض المهام المطلوبة والمكتملة حسب الشيف' : 'View assigned and completed tasks by chef'}
              </p>
            </div>
            
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                onClick={handleRefresh}
                icon={<RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />}
                size="md"
                disabled={refreshing}
              >
                {refreshing ? (isRtl ? 'جارٍ التحديث...' : 'Refreshing...') : (isRtl ? 'تحديث' : 'Refresh')}
              </Button>
              
              <CompactExportDropdown
                onExportPDF={handleExportPDF}
                onExportExcel={handleExportExcel}
                disabled={filteredChefsData.length === 0}
                isRtl={isRtl}
                showCompleted={showCompleted}
                onToggleShowCompleted={toggleShowCompleted}
              />
            </div>
          </div>
        </div>
        
        {!loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <StatsCard
              title={isRtl ? 'إجمالي الشيفات' : 'Total Chefs'}
              value={formatNumber(filteredChefsData.length, isRtl)}
              icon={<ChefHat className="w-5 h-5" />}
              color="blue"
              loading={loading}
              isRtl={isRtl}
            />
            <StatsCard
              title={isRtl ? 'إجمالي المنتجات' : 'Total Products'}
              value={formatNumber(allProducts.length, isRtl)}
              icon={<Package className="w-5 h-5" />}
              color="emerald"
              loading={loading}
              isRtl={isRtl}
            />
            <StatsCard
              title={isRtl ? 'الكمية المطلوبة' : 'Total Assigned Quantity'}
              value={formatNumber(
                chefsData.reduce((sum, chef) => sum + (chef.totalAssignedQuantity || 0), 0),
                isRtl
              )}
              icon={<Database className="w-5 h-5" />}
              color="amber"
              loading={loading}
              isRtl={isRtl}
            />
            <StatsCard
              title={isRtl ? 'الكمية المكتملة' : 'Total Completed Quantity'}
              value={formatNumber(
                chefsData.reduce((sum, chef) => sum + (chef.totalCompletedQuantity || 0), 0),
                isRtl
              )}
              icon={<CheckCircle className="w-5 h-5" />}
              color="green"
              loading={loading}
              isRtl={isRtl}
            />
          </div>
        )}
        
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5" dir={isRtl ? 'rtl' : 'ltr'}>
                {isRtl ? 'الفترة الزمنية' : 'Time Period'}
              </label>
              <Dropdown
                value={period}
                onChange={setPeriod}
                options={periodOptions}
                icon={<Calendar className="w-4 h-4" />}
                isRtl={isRtl}
              />
            </div>
            
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5" dir={isRtl ? 'rtl' : 'ltr'}>
                {isRtl ? 'الشيفات' : 'Chefs'}
              </label>
              <MultiSelectDropdown
                value={selectedChefsMulti}
                onChange={setSelectedChefsMulti}
                options={chefMultiSelectOptions}
                isRtl={isRtl}
                placeholder={isRtl ? 'اختر الشيفات...' : 'Select chefs...'}
              />
            </div>
            
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5" dir={isRtl ? 'rtl' : 'ltr'}>
                {isRtl ? 'ترتيب المنتجات' : 'Sort Products'}
              </label>
              <Dropdown
                value={sortBy}
                onChange={setSortBy}
                options={sortOptions}
                icon={<SortAsc className="w-4 h-4" />}
                isRtl={isRtl}
              />
            </div>
          </div>
          
          <div className="mt-4">
            <label className="block text-xs font-medium text-gray-700 mb-1.5" dir={isRtl ? 'rtl' : 'ltr'}>
              {isRtl ? 'بحث عن شيف' : 'Search for chef'}
            </label>
            <SearchInput
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={isRtl ? 'ابحث باسم الشيف أو البريد الإلكتروني...' : 'Search by chef name or email...'}
              isRtl={isRtl}
            />
          </div>
        </div>
        
        {loading ? (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
              <div className="h-64 bg-gray-200 rounded"></div>
            </div>
          </div>
        ) : (
          <>
            <div className="mb-4 flex flex-wrap gap-4">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-amber-100 border border-amber-300"></div>
                <span className="text-xs text-gray-700">{isRtl ? 'مطلوب' : 'Assigned'}</span>
              </div>
              {showCompleted && (
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded bg-emerald-100 border border-emerald-300"></div>
                  <span className="text-xs text-gray-700">{isRtl ? 'مكتمل' : 'Completed'}</span>
                </div>
              )}
              <div className="text-xs text-gray-500">
                {isChef 
                  ? (isRtl ? `إجمالي المطلوب والمكتمل` : `Total Assigned and Completed`)
                  : (isRtl ? `عرض توزيع المهام على الشيفات` : `Showing task distribution across chefs`)}
              </div>
            </div>
            
            {renderChefsTable()}
            
            <div className="mt-4 p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <p className="text-sm text-gray-600">
                    {displayedChefs.length} {isRtl ? 'شيف' : 'chef'} • {allProducts.length} {isRtl ? 'منتج' : 'product'}
                  </p>
                </div>
                <div className="flex items-center gap-6">
                  <div className="text-center">
                    <div className="text-sm font-medium text-gray-900">
                      {formatNumber(
                        chefsData.reduce((sum, chef) => sum + (chef.totalAssignedQuantity || 0), 0),
                        isRtl
                      )}
                    </div>
                    <div className="text-xs text-gray-600">
                      {isRtl ? 'إجمالي المطلوب' : 'Total Assigned'}
                    </div>
                  </div>
                  {showCompleted && (
                    <div className="text-center">
                      <div className="text-sm font-medium text-gray-900">
                        {formatNumber(
                          chefsData.reduce((sum, chef) => sum + (chef.totalCompletedQuantity || 0), 0),
                          isRtl
                        )}
                      </div>
                      <div className="text-xs text-gray-600">
                        {isRtl ? 'إجمالي المكتمل' : 'Total Completed'}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ProductionReport;